import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignContactModel, ContactModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignContact/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignContact/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignContact/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignContact/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignContact/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignContact/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignContact/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignContact/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignContact/DELETE_FAILURE'

export type CampaignContactModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
}

// Initial State
const initialState: CampaignContactModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignContacts(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignContacts.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_contacts.json', options)
      .then(({ data }: { data: CampaignContactModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_CONTACT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignContact(campaignContactId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignContacts.loadedForKeys]
    const loadedIds = [...state.campaignContacts.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignContactId && !loadedIds.includes(campaignContactId)){
      loadedIds.push(campaignContactId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_contacts/${campaignContactId}.json`, options)
      .then(({ data }: { data: CampaignContactModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_CONTACT)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignContact(campaignContact: Partial<CampaignContactModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_contact: campaignContact,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_contacts.json', options, config)
      .then(({ data }: { data: CampaignContactModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_CONTACT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createWithContact(
  campaignContact: Partial<CampaignContactModel>,
  contact: Partial<ContactModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_contact: campaignContact,
      contact,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_contacts/create_with_contact.json', options, config)
      .then(({ data }: { data: CampaignContactModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_CONTACT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCampaignContact(campaignContact: DeleteParams<CampaignContactModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_contacts/${campaignContact.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_CONTACT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignContactModuleState = initialState,
  action: Action = { type: '' },
): CampaignContactModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
